import { replayModeActive } from '@classes/app_url_params'
import canvas from '@classes/canvas'
import { getPropertyValue } from '@constants/utils'
import type { ILatLng } from '@shared_types/global_types'
import * as d3 from 'd3-geo'
import { type GeoProjection } from 'd3-geo'
import type { FeatureCollection } from 'geojson'
import { isValidGeolocation } from 'is-valid-geolocation'

import type { MainParams, StyleParams } from '../Meter'
import type { GaugeDrawResult } from './gauge_types'

export function drawEarthGauge (pos: ILatLng, main: MainParams, styke: StyleParams, world?: FeatureCollection): GaugeDrawResult {
  const { prevValue, value, maxValue, ctx, redraw, radius, dpx, center, currentValue } = main
  const { arcColor } = styke

  let newCurrentValue = currentValue

  const from = Number(prevValue)
  const to = Number(value)

  const zoom = replayModeActive() ? 3 : 1
  const color = replayModeActive() ? getPropertyValue('--gauge-wind1-color') : 'white'

  const drawing = (current: number) => {
    if (!!Number(maxValue) && current > Number(maxValue)) {
      newCurrentValue = Number(maxValue)
    } else {
      newCurrentValue = current
    }

    if (world && ctx) {
      ctx.clearRect(0, 0, ctx.canvas.width / 2, ctx.canvas.height / 2)
      redraw()
      const projection = d3
        .geoOrthographic()
        .scale(radius * zoom)
        .translate([(dpx * 100) / 2, (dpx * 100) / 2])
      const path = d3.geoPath(projection, ctx)
      rotateWorld(pos, projection)
      drawSeas(ctx, radius * zoom)
      drawLand(ctx, world, path)
      drawCirclePosition(ctx, path, pos, color)
      canvas.drawArc(ctx, center, center, radius, 0, Math.PI * 2, arcColor, dpx + 1)
    }
  }

  const animateId = canvas.animate(from, to, drawing, 2, 'globe')

  return { animateId, newCurrentValue }
}

const drawSeas = (ctx: CanvasRenderingContext2D, radius: number) => {
  ctx.beginPath()
  ctx.arc(ctx.canvas.width / 2, ctx.canvas.height / 2, radius, 0, 2 * Math.PI)
  ctx.strokeStyle = 'transparent'
  ctx.fill()
  ctx.stroke()
}

const drawLand = (ctx: CanvasRenderingContext2D, worldMap: FeatureCollection, path: d3.GeoPath) => {
  ctx.fillStyle = getPropertyValue('--meter-color')
  ctx.beginPath()
  if (worldMap) {
    path(worldMap)
  }
  ctx.fill()
  ctx.strokeStyle = 'transparent'
  ctx.stroke()
}

const drawCirclePosition = (
  ctx: CanvasRenderingContext2D,
  path: d3.GeoPath,
  pos: ILatLng,
  color = 'white'
) => {
  const position = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [pos.lng, pos.lat]
        }
      }
    ]
  }

  ctx.fillStyle = color
  path.pointRadius(2)

  position.features.forEach(feat => {
    ctx.beginPath()
    path(feat as d3.GeoPermissibleObjects)
    ctx.strokeStyle = color
    ctx.fill()
  })

  path.pointRadius(5)

  position.features.forEach(feat => {
    ctx.beginPath()
    path(feat as d3.GeoPermissibleObjects)
    ctx.strokeStyle = 'transparent'
    ctx.lineWidth = 1
    ctx.strokeStyle = color
    ctx.stroke()
  })

  path.pointRadius(8)

  position.features.forEach(feat => {
    ctx.beginPath()
    path(feat as d3.GeoPermissibleObjects)
    ctx.strokeStyle = 'transparent'
    ctx.lineWidth = 1
    ctx.strokeStyle = color
    ctx.stroke()
  })
}

const rotateWorld = (pos: ILatLng, projection: GeoProjection) => {
  if (isValidGeolocation(pos)) {
    projection.rotate([-pos.lng, -pos.lat])
  } else {
    projection.rotate([0, -30])
  }
}
